import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Building2, MapPin, Plug, AlertTriangle, ScrollText } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

/** Admin cockpit — platform health snapshot + recent audit trail.
 *  Uses existing admin endpoints (frontend-only composition). */
export default function AdminCockpit() {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [schemes, jurisdictions, connectors, events] = await Promise.all([
          api.get("/schemes").catch(() => ({ data: [] })),
          api.get("/admin/jurisdictions").catch(() => ({ data: [] })),
          api.get("/admin/connectors").catch(() => ({ data: [] })),
          api.get("/admin/connector-events?limit=20&status=FAILED").catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        const live = (jurisdictions.data || []).filter((j) => j.live).length;
        const now = Date.now();
        const recent24h = (events.data || []).filter((e) => {
          const t = e.created_at && new Date(e.created_at).getTime();
          return t && now - t < 24 * 3600 * 1000;
        }).length;

        // Recent confirmations (immutable audit trail) — try /admin/audit fallback to empty
        const conf = await api.get("/admin/confirmations?limit=10").catch(() => ({ data: [] }));

        setState({
          loading: false,
          schemesCount: (schemes.data || []).length,
          liveJurisdictions: live,
          totalJurisdictions: 8,
          connectorsCount: (connectors.data || []).length,
          recentErrors: recent24h,
          confirmations: (conf.data || []).slice(0, 10),
        });
      } catch (_e) {
        if (mounted) setState({ loading: false, err: true });
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (state.loading) return <AppShell><div className="max-w-6xl mx-auto px-6 py-8"><div className="h-64 rounded-xl border animate-pulse bg-slate-50" style={{ borderColor: "var(--border)" }} /></div></AppShell>;

  const stats = [
    { icon: Building2, label: "Schemes active", value: state.schemesCount, testid: "admin-stat-schemes" },
    { icon: MapPin, label: "Jurisdictions LIVE", value: `${state.liveJurisdictions} / ${state.totalJurisdictions}`, testid: "admin-stat-jurisdictions" },
    { icon: Plug, label: "Connectors registered", value: state.connectorsCount, testid: "admin-stat-connectors" },
    { icon: AlertTriangle, label: "Errors · last 24h", value: state.recentErrors, tone: state.recentErrors > 0 ? "alert" : "ok", testid: "admin-stat-errors" },
  ];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8" data-testid="admin-cockpit">
        <h1 className="text-2xl font-semibold" style={{ color: "var(--navy)" }}>Platform health</h1>
        <p className="text-sm text-slate-500 mt-1">A snapshot of scheme, jurisdiction, connector, and audit state.</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => {
            const Icon = s.icon;
            const color = s.tone === "alert" ? "var(--alert)" : "var(--navy)";
            return (
              <div key={s.label} className="rounded-xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid={s.testid}>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Icon className="w-3.5 h-3.5" /> {s.label}</div>
                <div className="mt-3 text-4xl font-bold tabular-nums" style={{ color }}>{s.value}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 rounded-xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid="admin-recent-audit">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">
            <ScrollText className="w-3.5 h-3.5" /> Recent audit activity (immutable confirmations)
          </div>
          {state.confirmations.length === 0 ? (
            <div className="text-sm text-slate-500">
              No confirmations in this window — try{" "}
              <Link to="/admin/legislation-versions" className="font-semibold" style={{ color: "var(--blue)" }}>Legislation Versions</Link>{" "}or{" "}
              <Link to="/admin/connectors" className="font-semibold" style={{ color: "var(--blue)" }}>Connectors</Link>.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {state.confirmations.map((c) => (
                <div key={c.confirmation_id || c.id} className="py-2.5 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-semibold" style={{ color: "var(--navy)" }}>{c.action_type || c.type}</span>
                    <span className="text-slate-500"> · scheme {c.scheme_id?.slice(-6) || "—"} · user {(c.user_id || "").slice(-6)}</span>
                  </div>
                  <div className="text-xs text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { to: "/admin/jurisdictions", label: "Jurisdictions", body: "Toggle LIVE per state" },
            { to: "/admin/connectors", label: "Connectors", body: "Registry + scheme-level status" },
            { to: "/admin/legislation-versions", label: "Legislation versions", body: "Publish new legislation revisions" },
          ].map((l) => (
            <Link key={l.to} to={l.to} className="rounded-xl border bg-white p-5 hover:border-[color:var(--blue)] transition-colors" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--blue)" }}>
                <Activity className="w-3.5 h-3.5" /> {l.label}
              </div>
              <div className="mt-2 text-sm text-slate-700">{l.body}</div>
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
